import { computed, makeObservable, observable, runInAction, action } from "mobx";
import { OnboardingStage, PlatformOnboardingState } from "@/store/platform-info/types";
import { ApiSchema } from "@/modules/api";
import { PlatformOnboardingInfoProfileObservable } from "@/store/platform-info/PlatformOnboardingInfoProfileObservable";
import { isNull } from "lodash-es";
import { WithAppStore } from "@/store/types";
import { AppStore } from "@/store";
import { StepGroups } from "@/components/onboarding/components/WelcomeWizard/WelcomeWizard.config";
import type { StepGroup } from "@/components/onboarding/components/WelcomeWizard/WelcomeWizard.types";

export type WelcomeStartStep = "user-profile" | "app-selection";

export class PlatformOnboardingInfoObservable {
  store: AppStore;
  onboardingState: PlatformOnboardingState;
  onboardingProfile: PlatformOnboardingInfoProfileObservable;
  activeOnboardingStage: OnboardingStage;
  shouldShowIcpRfi: boolean;
  shouldShowImportCta: boolean;
  welcomeStartStep: WelcomeStartStep;
  rfiCompletedSteps: string[];

  constructor({
    onboardingState,
    onboardingProfile,
    shouldShowIcpRfi,
    shouldShowImportCta,
    rfiStartStep = "user-profile",
    rfiCompletedSteps = [],
    store,
  }: {
    onboardingState: PlatformOnboardingState;
    onboardingProfile: PlatformOnboardingInfoProfileObservable;
    shouldShowIcpRfi?: boolean;
    shouldShowImportCta?: boolean;
    rfiStartStep?: WelcomeStartStep;
    rfiCompletedSteps?: string[];
  } & WithAppStore) {
    this.onboardingState = onboardingState;
    this.onboardingProfile = onboardingProfile;
    this.store = store;
    this.shouldShowIcpRfi = shouldShowIcpRfi ?? false;
    this.shouldShowImportCta = shouldShowImportCta ?? false;
    this.welcomeStartStep = rfiStartStep;
    this.rfiCompletedSteps = rfiCompletedSteps;

    this.activeOnboardingStage = PlatformOnboardingInfoObservable.calculateOnboardingStage({
      onboardingProfile: this.onboardingProfile,
    });

    makeObservable(this, {
      store: false,
      onboardingState: observable,
      onboardingProfile: observable,
      activeOnboardingStage: observable,
      shouldShowIcpRfi: observable,
      shouldShowImportCta: observable,
      welcomeStartStep: observable,
      rfiCompletedSteps: observable,
      onboardingIsCompleted: computed,
      shouldShowWelcomeOverlay: computed,
      welcomeStepGroups: computed,
      welcomeInitialStep: computed,
      setDesiredUseCases: action,
      setJobRole: action,
      setReferrer: action,
      saveAndContinue: action,
      markStepCompleted: action,
      setWelcomeStartStep: action,
    });
  }

  get onboardingIsCompleted() {
    return this.onboardingState === PlatformOnboardingState.OnboardingCompleted;
  }

  get shouldShowWelcomeOverlay() {
    return this.shouldShowIcpRfi || this.shouldShowImportCta;
  }

  get welcomeStepGroups(): StepGroup[] {
    const stepGroups: StepGroup[] = [];

    if (this.shouldShowIcpRfi) {
      stepGroups.push(StepGroups.ICP_RFI);
    }

    if (this.shouldShowImportCta) {
      stepGroups.push(StepGroups.IMPORT);
    }

    return stepGroups;
  }

  get welcomeInitialStep(): WelcomeStartStep {
    // If only import CTA is relevant, start with app-selection
    if (!this.shouldShowIcpRfi && this.shouldShowImportCta) {
      return "app-selection";
    }

    // Otherwise, start with user-profile (or the configured step)
    return this.welcomeStartStep;
  }

  setDesiredUseCases({ desiredUseCases }: { desiredUseCases: string[] }) {
    runInAction(() => {
      this.onboardingProfile.desiredUseCases = desiredUseCases;
    });
  }

  setJobRole({ jobRole }: { jobRole: string }) {
    runInAction(() => {
      this.onboardingProfile.jobRole = jobRole;
    });
  }

  setReferrer({ referrer }: { referrer: string }) {
    runInAction(() => {
      this.onboardingProfile.referrer = referrer;
    });
  }

  markStepCompleted(stepId: string) {
    runInAction(() => {
      if (!this.rfiCompletedSteps.includes(stepId)) {
        this.rfiCompletedSteps.push(stepId);
      }
    });
  }

  setWelcomeStartStep(stepId: WelcomeStartStep) {
    runInAction(() => {
      this.welcomeStartStep = stepId;
    });
  }

  async saveAndContinue() {
    await this.store.platformInfo.upsertOnboardingProfile({
      onboardingProfile: this.onboardingProfile,
    });

    runInAction(() => {
      this.activeOnboardingStage = PlatformOnboardingInfoObservable.calculateOnboardingStage({
        onboardingProfile: this.onboardingProfile,
      });
    });
  }

  public static calculateOnboardingStage({
    onboardingProfile,
  }: {
    onboardingProfile: PlatformOnboardingInfoProfileObservable;
  }) {
    if (isNull(onboardingProfile.desiredUseCases)) {
      return OnboardingStage.SelectUseCases;
    }

    if (isNull(onboardingProfile.jobRole)) {
      return OnboardingStage.SelectJob;
    }

    if (isNull(onboardingProfile.referrer)) {
      return OnboardingStage.SelectReferrer;
    }

    return OnboardingStage.SelectReferrer;
  }

  public static createFromRemoteData({
    data,
    store,
  }: { data: ApiSchema["PlatformInfoOnboarding"] } & WithAppStore) {
    const onboardingProfile = PlatformOnboardingInfoProfileObservable.createFromRemoteData({
      data: data.onboarding_profile,
    });

    // Type-safe property access with fallbacks for new RFI properties
    type ExtendedOnboardingData = ApiSchema["PlatformInfoOnboarding"] & {
      should_show_rfi?: boolean;
      should_show_icp_rfi?: boolean;
      should_show_import_cta?: boolean;
      rfi_start_step?: WelcomeStartStep;
      rfi_completed_steps?: string[];
    };
    const extendedData = data as ExtendedOnboardingData;

    return new PlatformOnboardingInfoObservable({
      onboardingState: data.onboarding_state as PlatformOnboardingState,
      onboardingProfile: onboardingProfile,
      shouldShowIcpRfi: extendedData.should_show_icp_rfi ?? extendedData.should_show_rfi ?? false,
      shouldShowImportCta:
        extendedData.should_show_import_cta ?? data.should_show_import_cta ?? false,
      rfiStartStep: extendedData.rfi_start_step ?? "user-profile",
      rfiCompletedSteps: extendedData.rfi_completed_steps ?? [],
      store,
    });
  }
}
